import {
	freeServicesAtom,
	rawAiCostBreakdownAtom,
	rawSeasonalPricingAtom,
	serviceAvailabilityAtom,
	startingCostAtom,
} from '@client/jotai/storefront/pricing';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import {
	transformAiCostBreakdownToPricingEstimation,
	usePricingEstimation,
} from '../../../../hooks/use-pricing-estimation';
import type {
	PricingEstimationCostBreakdown,
	PricingEstimationService,
	PricingEstimationState,
	PricingEstimationTotal,
	PricingEstimationType,
} from '../../../../types';
import { parseStartingCostToNumber } from '../../../../utils/pricing-estimation';
import type { WeddingDateData } from '../../../utils/weddingDate';

const SERVICE_TO_CATEGORY: Record<
	PricingEstimationService,
	PricingEstimationService
> = {
	ceremony: 'ceremony',
	reception: 'reception',
	catering: 'catering',
	barServices: 'barServices',
};

const toFixedPricing = (
	value: number | null | undefined,
	unit: 'TOTAL' | 'PER_GUEST' = 'TOTAL',
	isFree = false,
): PricingEstimationType =>
	value != null
		? {
				cost: value,
				unit,
				currency: 'USD',
				dataSource: 'VENDOR',
				...(isFree ? { isFree: true } : {}),
			}
		: null;

// Validates and narrows a raw unit string from the API
const resolveUnit = (
	unit: string | null | undefined,
	fallback: 'TOTAL' | 'PER_GUEST',
): 'TOTAL' | 'PER_GUEST' => {
	if (unit === 'PER_GUEST' || unit === 'TOTAL') return unit;
	return fallback;
};

const parseDateComponents = (
	weddingDate: WeddingDateData,
): { year: number | null; month: number | null; day: number | null } => {
	if (weddingDate.dateOption === 'specificDate' && weddingDate.specificDate) {
		const [yearStr, monthStr, dayStr] = weddingDate.specificDate.split('-');
		return {
			year: Number(yearStr),
			month: Number(monthStr) - 1,
			day: Number(dayStr),
		};
	}

	if (weddingDate.dateOption === 'monthYear' && weddingDate.month) {
		const monthIndex = new Date(`${weddingDate.month} 1, 2000`).getMonth();
		return {
			year: weddingDate.year ? Number(weddingDate.year) : null,
			month: monthIndex,
			day: null,
		};
	}

	return { year: null, month: null, day: null };
};

export const useCalculateEstimate = (
	guestCount: string,
	weddingDate: WeddingDateData,
	selectedServices: PricingEstimationService[],
): PricingEstimationTotal | null => {
	const rawSeasonal = useAtomValue(rawSeasonalPricingAtom);
	const aiCostBreakdown = useAtomValue(rawAiCostBreakdownAtom);
	const startingCost = useAtomValue(startingCostAtom);
	const serviceAvailability = useAtomValue(serviceAvailabilityAtom);
	const freeServices = useAtomValue(freeServicesAtom);

	const pricingEstimations = useMemo((): (
		| PricingEstimationState
		| PricingEstimationCostBreakdown
	)[] => {
		if (!rawSeasonal && !aiCostBreakdown && freeServices.length === 0)
			return [];

		const { peak, offPeak } = rawSeasonal || {};

		const ceremonyUnit = resolveUnit(
			aiCostBreakdown?.ceremony?.price?.unit,
			'TOTAL',
		);
		const receptionUnit = resolveUnit(
			aiCostBreakdown?.reception?.price?.unit,
			'TOTAL',
		);
		const barServicesUnit = resolveUnit(
			aiCostBreakdown?.barServices?.price?.unit,
			'PER_GUEST',
		);
		const cateringUnit = resolveUnit(
			aiCostBreakdown?.catering?.price?.unit,
			'PER_GUEST',
		);

		const rawSeasonalToPricingEstimation = {
			peak: {
				ceremony: freeServices.includes('ceremony')
					? toFixedPricing(0, ceremonyUnit, true)
					: toFixedPricing(peak?.services?.ceremony, ceremonyUnit),
				reception: freeServices.includes('reception')
					? toFixedPricing(0, receptionUnit, true)
					: toFixedPricing(peak?.services?.reception, receptionUnit),
				catering: toFixedPricing(peak?.services?.catering, cateringUnit),
				barServices: toFixedPricing(
					peak?.services?.barServices,
					barServicesUnit,
				),
			},
			offPeak: {
				ceremony: freeServices.includes('ceremony')
					? toFixedPricing(0, ceremonyUnit, true)
					: toFixedPricing(offPeak?.services?.ceremony, ceremonyUnit),
				reception: freeServices.includes('reception')
					? toFixedPricing(0, receptionUnit, true)
					: toFixedPricing(offPeak?.services?.reception, receptionUnit),
				catering: toFixedPricing(offPeak?.services?.catering, cateringUnit),
				barServices: toFixedPricing(
					offPeak?.services?.barServices,
					barServicesUnit,
				),
			},
			peakMonths: peak?.services?.months ?? null,
			offPeakMonths: offPeak?.services?.months ?? null,
		};

		const costBreakdownToPricingEstimation =
			transformAiCostBreakdownToPricingEstimation(aiCostBreakdown ?? null);

		return [rawSeasonalToPricingEstimation, costBreakdownToPricingEstimation];
	}, [rawSeasonal, aiCostBreakdown, freeServices]);

	const selectedCategories = useMemo(
		() =>
			selectedServices
				.filter((service) => serviceAvailability[service])
				.map((s) => SERVICE_TO_CATEGORY[s]),
		[selectedServices, serviceAvailability],
	);

	const parsedStartingCost = useMemo(
		() => parseStartingCostToNumber(startingCost),
		[startingCost],
	);

	const { year, month, day } = parseDateComponents(weddingDate);

	return usePricingEstimation({
		guestCount: Number(guestCount) || 0,
		year,
		month,
		day,
		selectedCategories,
		pricingEstimations,
		// The storefront starting cost is a universal minimum, regardless of the
		// selected service subset.
		startingCost: parsedStartingCost,
	});
};
